import lunr from 'lunr'
import client from 'lib/graphql'
import gql from 'graphql-tag'
import pDebounce from 'p-debounce'

import { sourcePath, sourcesCatalogItemPath } from 'lib/path-helpers/sources'
import { warehousePath } from 'lib/path-helpers/warehouses'
import {
  destinationSettingsPath,
  destinationsCatalogWarehouseItemPath,
  destinationsCatalogItemPath
} from 'lib/path-helpers/destinations'

const metadataQuery = gql`
  query app_omnisearch_all_metadata($workspaceSlug: Slug!) {
    integrationMetadatas(workspaceSlug: $workspaceSlug) {
      id
      slug
      name
      status
      logos {
        default
        mark
      }
    }
    sourceMetadatas {
      id
      slug
      name
      visibility
      logos {
        default
        mark
      }
    }
    warehouseMetadatas {
      id
      slug
      name
      public
      logos {
        default
        mark
      }
    }
  }
`

const destinationsQuery = gql`
  query app__omnisearch_destinations($slug: Slug!) {
    workspace(slug: $slug) {
      id
      sources {
        id
        slug
        name
        integrations {
          id
          enabled
          source {
            id
            name
            slug
          }
          metadata {
            id
            name
            slug
            logos {
              mark
              default
            }
          }
        }
      }
    }
  }
`
const warehousesQuery = gql`
  query app__omnisearch_warehouses($slug: Slug!) {
    workspace(slug: $slug) {
      id
      slug
      name
      warehouses {
        id
        enabled
        settings {
          name
        }
        metadata {
          id
          name
          logos {
            mark
            default
          }
        }
      }
    }
  }
`

const sourcesQuery = gql`
  query app__omnisearch_sources($slug: Slug!) {
    workspace(slug: $slug) {
      id
      slug
      name
      sources {
        id
        slug
        name
        enabled
        writeKeys
        metadata {
          name
          id
          category
          logos {
            mark
            default
          }
        }
      }
    }
  }
`

function getCategoryFromMetadataType(metadataType) {
  if (metadataType === 'SourceMetadata') {
    return 'sources'
  }
  if (metadataType === 'IntegrationMetadata') {
    return 'destinations'
  }
  if (metadataType === 'WarehouseMetadata') {
    return 'warehouses'
  }
}

function getPath(workspaceSlug, item) {
  const type = item.type
  if (type === 'source') {
    return sourcePath({ sourceSlug: item.slug, workspaceSlug })
  }

  if (type === 'destination') {
    return destinationSettingsPath({
      destinationMetadataSlug: item.metadata.slug,
      workspaceSlug,
      sourceSlug: item.source.slug
    })
  }

  if (type === 'catalog' && item.category === 'warehouses') {
    return destinationsCatalogWarehouseItemPath({
      workspaceSlug,
      warehouseMetadataSlug: item.slug
    })
  }

  if (type === 'catalog' && item.category === 'destinations') {
    return destinationsCatalogItemPath({
      workspaceSlug,
      destinationMetadataSlug: item.slug
    })
  }

  if (type === 'catalog' && item.category === 'sources') {
    return sourcesCatalogItemPath({
      workspaceSlug,
      sourceMetadataSlug: item.slug
    })
  }

  if (type === 'warehouse') {
    return warehousePath({ workspaceSlug, warehouseId: item.id })
  }
}

class OmnisearchClient {
  constructor(user) {
    this.searches = {}
    this.documents = {}
    this.done = false
    this.OMNISEARCH_MUTATIONS = [
      'updateIntegrationSettings',
      'UpdateIntegrationSettings',
      'createSource',
      'deleteSource',
      'removeDestination',
      'toggleIntegrationEnabled',
      'createWarehouse',
      'deleteWarehouse',
      'updateWarehouse'
    ]
  }

  reset() {
    this.searches = {}
    this.documents = {}
    this.done = false
  }

  async init(slug, isAdmin) {
    this.slug = slug
    this.isAdmin = isAdmin
    await this.createIndices()
  }

  debouncedRefreshIndices = pDebounce(this.refreshIndices, 1000)

  async refreshIndices(mutation) {
    if (!this.OMNISEARCH_MUTATIONS.includes(mutation)) {
      return
    }

    this.reset()
    await this.createIndices()
  }

  async createIndices() {
    if (this.done) {
      return
    }

    const slug = this.slug
    const isAdmin = this.isAdmin

    if (!slug) {
      return
    }

    const sourcesPromise = client
      .query({
        query: sourcesQuery,
        variables: {
          slug
        }
      })
      .then(res => {
        this.processSources(res)
      })
      .catch(e => {})

    const warehousesPromise = client
      .query({
        query: warehousesQuery,
        variables: {
          slug
        }
      })
      .then(res => {
        this.processWarehouses(res)
      })
      .catch(e => {})

    const destinationsPromise = client
      .query({
        query: destinationsQuery,
        variables: {
          slug
        }
      })
      .then(res => this.processDestinations(res))
      .catch(e => {
        // console.error('e', e)
      })
    const metadataPromise = client
      .query({
        query: metadataQuery,
        variables: {
          workspaceSlug: slug
        }
      })
      .then(res => {
        this.processMetadata(res, isAdmin)
      })
      .catch(e => {})

    await Promise.all([
      sourcesPromise,
      destinationsPromise,
      warehousesPromise,
      metadataPromise
    ])

    this.done = true
  }

  processMetadata({ data }, isAdmin) {
    let { integrationMetadatas, sourceMetadatas, warehouseMetadatas } = data

    if (!isAdmin) {
      integrationMetadatas = integrationMetadatas.filter(
        metadata =>
          metadata.status === 'PUBLIC' || metadata.status === 'PUBLIC_BETA'
      )

      sourceMetadatas = sourceMetadatas.filter(
        metadata =>
          metadata.visibility === 'live' || metadata.visibility === 'beta'
      )

      warehouseMetadatas = warehouseMetadatas.filter(metadata =>
        Boolean(metadata.public)
      )
    }

    const metadata = integrationMetadatas
      .concat(sourceMetadatas)
      .concat(warehouseMetadatas)

    const metaDocs = []
    const metaRefs = metadata.reduce((refs, m) => {
      const metaDoc = {
        id: m.id,
        slug: m.slug,
        name: m.name,
        type: 'catalog',
        searchName: m.name.toLowerCase(),
        category: getCategoryFromMetadataType(m.__typename),
        logo: m.logos.mark || m.logos.default
      }

      metaDoc.path = getPath(this.slug, metaDoc)

      refs[m.slug] = metaDoc
      metaDocs.push(metaDoc)
      return refs
    }, {})

    const metaIndex = lunr(function() {
      this.ref('slug')
      this.field('name')
      this.field('slug')
      this.field('type')
      this.field('searchName')

      metaDocs.forEach(function(doc) {
        this.add(doc)
      }, this)
    })

    this.searches.catalog = metaIndex
    this.documents.catalog = metaRefs
  }

  processSources({ data }) {
    const sources = data.workspace.sources
    const sourceDocs = []
    const sourceRefs = sources.reduce((refs, s) => {
      const { metadata } = s
      const sourceDoc = {
        id: s.id,
        slug: s.slug,
        name: metadata.name,
        metadataName: metadata.name,
        instanceName: s.name,
        type: 'source',
        enabled: s.enabled,
        searchName: s.name.toLowerCase(),
        logo: metadata.logos.mark || metadata.logos.default,
        category: metadata.category,
        writeKeys: s.writeKeys
      }
      refs[s.slug] = sourceDoc
      sourceDoc.path = getPath(this.slug, sourceDoc)
      sourceDocs.push(sourceDoc)
      return refs
    }, {})

    const sourceIndex = lunr(function() {
      this.ref('slug')
      this.field('name')
      this.field('slug')
      this.field('type')
      this.field('searchName')
      this.field('metadataName')
      this.field('writeKeys')

      sourceDocs.forEach(function(doc) {
        this.add(doc)
      }, this)
    })

    this.searches.source = sourceIndex
    this.documents.source = sourceRefs
  }

  processDestinations({ data }) {
    const sources = data.workspace.sources
    const destinations = sources.reduce((destinations, s) => {
      return [...destinations, ...s.integrations]
    }, [])

    const destinationDocs = []
    const destinationRefs = destinations.reduce((refs, d) => {
      const { metadata, source, enabled } = d
      const destinationDoc = {
        id: d.id,
        name: metadata.name,
        instanceName: metadata.name,
        metadataName: metadata.name,
        source,
        sourceSlug: source.slug,
        enabled,
        type: 'destination',
        searchName: metadata.name.toLowerCase(),
        logo: metadata.logos.mark || metadata.logos.default,
        metadata
      }

      refs[d.id] = destinationDoc
      destinationDoc.path = getPath(this.slug, destinationDoc)
      destinationDocs.push(destinationDoc)
      return refs
    }, {})

    const destinationIndex = lunr(function() {
      this.ref('id')
      this.field('name')
      this.field('sourceSlug')
      this.field('searchName')
      this.field('metadataName')

      destinationDocs.forEach(function(doc) {
        this.add(doc)
      }, this)
    })
    this.searches.destination = destinationIndex
    this.documents.destination = destinationRefs
  }

  processWarehouses({ data }) {
    const warehouses = data.workspace.warehouses

    const warehouseDocs = []
    const warehouseRefs = warehouses.reduce((refs, w) => {
      const { metadata, settings, enabled } = w
      const warehouseDoc = {
        id: w.id,
        instanceName: settings.name,
        name: metadata.name,
        metadataName: metadata.name,
        metadata,
        type: 'warehouse',
        searchName: metadata.name.toLowerCase(),
        logo: metadata.logos.mark || metadata.logos.default,
        enabled
      }

      warehouseDoc.path = getPath(this.slug, warehouseDoc)
      refs[w.id] = warehouseDoc
      warehouseDocs.push(warehouseDoc)
      return refs
    }, {})

    const warehouseIndex = lunr(function() {
      this.ref('id')
      this.field('name')
      this.field('searchName')
      this.field('metadataName')
      this.field('instanceName')

      warehouseDocs.forEach(function(doc) {
        this.add(doc)
      }, this)
    })
    this.searches.warehouse = warehouseIndex
    this.documents.warehouse = warehouseRefs
  }

  search(term) {
    if (!this.done) {
      return {}
    }

    const allSearchResults = {}
    const categories = ['source', 'destination', 'warehouse', 'catalog']

    categories.forEach(category => {
      if (!this.searches[category]) {
        return
      }

      const search = this.searches[category]

      // ~2 for extra fuzzyness
      const searchTerm = getSearchTerm(term)
      const searchRefs = search.search(searchTerm).slice(0, 3)

      const searchResults = searchRefs.map(ref => {
        return this.documents[category][ref.ref]
      })

      allSearchResults[category] = searchResults
    })

    return allSearchResults
  }

  /**
   * Browses a certain indexName based on a searchable field
   *
   * @param {term} - The search term you want to find within the index
   * @param {indexName} - The searchble index that should be used
   */
  async browseIndex({ term, indexName }) {
    if (!this.done) {
      return {}
    }

    const search = this.searches[indexName]

    const searchRefs = search.search(term)

    const searchResults = []
    searchRefs.forEach(ref => {
      searchResults.push(this.documents[indexName][ref.ref])
    })

    return searchResults
  }
}

const getSearchTerm = term => {
  term = term.trim()
  // Will need to come back and think about : behavior
  const tokens = term.split(/:| /).filter(term => term !== '')
  if (tokens.length > 1) {
    return tokens.slice(1).reduce((searchTerm, token) => {
      return `${searchTerm} ${token.trim()}\\ `
    }, tokens[0].trim())
  }

  term = term.replace(':', '')
  return `${lowercase(term)}* ${lowercase(term)}`
}

const lowercase = s => s.toLowerCase()

export default new OmnisearchClient()
